import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-8 md:gap-6" }
const _hoisted_2 = ["onClick"]

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { IDropdownItem, IFormStep } from '@/interfaces';
import { IQConfigSettings, SettingCategory, useConfigSettingsStore, useReferenceDataIQMotorStore } from '@/store';
import { FormKit } from '@formkit/vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import AddRemoveForm from '@/components/AddRemoveForm/AddRemoveForm.vue';
import useFormInputData from '@/composables/useFormInputData';
import { toBool } from '@/utils/convert';
import { findFieldContent } from '@/utils/common-helper';

// Define props and emitters
// ------------------------------------------------------------- //

export default /*@__PURE__*/_defineComponent({
  __name: 'CarAccessories',
  props: /*@__PURE__*/_mergeModels({
    step: {},
    initialAccessories: { default: () => [''] }
  }, {
    "modelValue": { required: true },
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any) {

const props = __props;
const formData = _useModel<Record<string, any>>(__props, "modelValue");

// Define Store
// ------------------------------------------------------------- //
const refMotorStore = useReferenceDataIQMotorStore();
const configSettingsConfig = useConfigSettingsStore();

// Define Composables
// ------------------------------------------------------------- //
const { formInputData } = useFormInputData(props.step.formInputs!);

// Define Component state
// ------------------------------------------------------------- //
const initialAccessories = ref<string[]>(props.initialAccessories);
const list = ref<IDropdownItem[]>([]);
const maxAdditionalAccessories = ref(50);
const accessoryItemLabel = ref('');
const addButtonText = ref('');
const removeButtonText = ref('');
const dropdownLabelText = ref('');
const dropdownPlaceholderText = ref('');
const dropdownValidationErrorMessage = ref('');

const filteredList = computed(
  () => (self: string) =>
    list.value.filter((item) => item.value === self || !initialAccessories.value.includes(item.value))
);

const renderInput = computed(() => (self: string) => !self || list.value.some((item) => item.value === self));

const showAddButton = computed(() => (index: number) => {
  return (
    index === initialAccessories.value.length - 1 && initialAccessories.value.length < maxAdditionalAccessories.value
  );
});

watch(
  () => props.initialAccessories,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      initialAccessories.value = newValue;
    }
  }
);

onMounted(async () => {
  const [accessoryData, maxAccessoriesSetting] = await Promise.all([
    refMotorStore.getAccessoriesAndModification(),
    configSettingsConfig.getSetting(IQConfigSettings.MaxAdditionalAccessories, SettingCategory.IQSettings),
  ]);
  list.value = accessoryData.map((item) => ({ label: item.name, value: item.value }));
  maxAdditionalAccessories.value = (maxAccessoriesSetting as number) || 50;
  const fields = props.step.formInputs?.hasAccessories.fields || [];
  accessoryItemLabel.value = findFieldContent(fields, 'AccessoryItemLabel');
  addButtonText.value = findFieldContent(fields, 'AddButtonText');
  removeButtonText.value = findFieldContent(fields, 'RemoveButtonText');
  dropdownLabelText.value = findFieldContent(fields, 'DropdownLabelText');
  dropdownPlaceholderText.value = findFieldContent(fields, 'DropdownPlaceholderText');
  dropdownValidationErrorMessage.value = findFieldContent(fields, 'DropdownValidationErrorMessage');
});

function addAccessory() {
  initialAccessories.value.push('');
}

function removeAccessory(index: number) {
  const updatedAccessories = [...initialAccessories.value];
  updatedAccessories.splice(index, 1);

  // Ensure Vue detects the change
  initialAccessories.value = [];
  nextTick(() => {
    initialAccessories.value = updatedAccessories;
    formData.value.accessories = [...updatedAccessories];

    if (updatedAccessories.length === 0) {
      formData.value.hasAccessories = 'false';
    }
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(RACQFormKit, {
      bindings: _unref(formInputData)(_ctx.step.formInputs?.hasAccessories?.formInputId?.value)
    }, null, 8, ["bindings"]),
    (_unref(toBool)(formData.value.hasAccessories))
      ? (_openBlock(), _createBlock(_unref(FormKit), {
          key: 0,
          type: "list",
          name: "accessories"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(initialAccessories.value, (_, index) => {
                return (_openBlock(), _createBlock(AddRemoveForm, {
                  key: index,
                  title: { value: `${accessoryItemLabel.value} ${index + 1}` }
                }, {
                  buttonAction: _withCtx(() => [
                    _createElementVNode("button", {
                      type: "button",
                      class: "link text-base-bold self-baseline",
                      onClick: _withModifiers(($event: any) => (removeAccessory(index)), ["prevent"])
                    }, _toDisplayString(removeButtonText.value), 9, _hoisted_2)
                  ]),
                  sectionPanelContent: _withCtx(() => [
                    (renderInput.value(initialAccessories.value[index]))
                      ? (_openBlock(), _createBlock(_unref(FormKit), {
                          key: 0,
                          options: filteredList.value(initialAccessories.value[index]),
                          openOnClick: "",
                          clearSearchOnOpen: "",
                          placeholder: dropdownPlaceholderText.value,
                          validation: "required",
                          "aria-label": "Accessories List",
                          title: "Accessories List",
                          outerClass: "input-md !h-fit",
                          wrapperClass: "$reset md:block",
                          inputClass: "text-ellipsis whitespace-nowrap",
                          label: dropdownLabelText.value,
                          closeIcon: false,
                          labelClass: "hidden",
                          validationMessages: { required: dropdownValidationErrorMessage.value },
                          type: "autocomplete"
                        }, null, 8, ["options", "placeholder", "label", "validationMessages"]))
                      : _createCommentVNode("", true)
                  ]),
                  addButton: _withCtx(() => [
                    (showAddButton.value(index))
                      ? (_openBlock(), _createBlock(_unref(FormKit), {
                          key: 0,
                          type: "button",
                          inputClass: "btn btn-secondary btn-md input-cta mt-3",
                          label: addButtonText.value,
                          onClick: addAccessory
                        }, null, 8, ["label"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["title"]))
              }), 128))
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})